import moment from "moment";
import React, { useEffect, useState } from "react";
import { SingleDatePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./datePicker.module.scss";
import { Nullable } from "features/shared/types/nullable";
import { useTranslation } from "react-i18next";

const DATE_FORMAT = "DD.MM.YYYY";
interface Props {
  name: string;
  onChange: (date: Nullable<moment.Moment>) => void;
  selectedDate?: Nullable<Date>;
  minDate?: Nullable<Date>;
  maxDate?: Nullable<Date>;
  onlySelectable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showYearsSelect?: boolean;
  isDayOutsideOfRange?: (day: Date) => boolean;
  initialVisibleDate?: () => Date;
  isClearable?: boolean;
}

const DatePicker = ({
  name,
  onChange,
  selectedDate,
  minDate,
  maxDate,
  onlySelectable = false,
  placeholder,
  disabled = false,
  showYearsSelect = false,
  isDayOutsideOfRange,
  initialVisibleDate,
  isClearable = false
}: Props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;

  // state
  const [focus, setFocus] = useState(false);
  const [date, setDate] = useState<Nullable<moment.Moment>>(null);

  // update date
  useEffect(() => {
    if (selectedDate) {
      setDate(moment(selectedDate).startOf("day"));
    } else {
      setDate(null);
    }
  }, [selectedDate]);

  // handlers
  const focusChangeHandler = (arg: { focused: boolean | null }) => {
    let focused = arg.focused ? arg.focused : false;
    setFocus(focused);
  };

  const dateChangeHandler = (date: Nullable<moment.Moment>) => {
    setDate(date);

    if (date) {
      onChange(date.startOf("day"));
    } else {
      // support for clearing date picker
      onChange(null);
    }
  };

  // helpers
  const isOutsideOfRange = (day: moment.Moment): boolean => {
    if (isDayOutsideOfRange) {
      return isDayOutsideOfRange(day.toDate());
    } else if (minDate || maxDate) {
      if (minDate || maxDate) {
        const dateFrom = getFromDate();
        const dateTo = getToDate();

        return !day.isBetween(dateFrom, dateTo);
      }
    }

    return false;
  };

  const getFromDate = () => {
    const dateFrom = minDate
      ? moment(minDate).startOf("day")
      : moment("1900-01-01");

    return dateFrom;
  };

  const getToDate = () => {
    const dateTo = maxDate
      ? moment(maxDate).endOf("day")
      : moment("2199-12-31");

    return dateTo;
  };

  const getYears = (): number[] => {
    const fromYear = getFromDate().year();
    const toYear = getToDate().year();
    let years: number[] = [];

    for (let i = fromYear; i <= toYear; i++) {
      years.push(i);
    }

    return years;
  };


  const initialVisibleMonth = (): moment.Moment => { 
    if (initialVisibleDate) {
      if (date === null) {
        return moment(initialVisibleDate());   
      } else {
        return date;
      }
    }
    
    return getFromDate();
  }

  // sub-renders
  const renderMonthElement = (props: {
    month: moment.Moment;
    onMonthSelect: (currentMonth: moment.Moment, newMonthVal: string) => void;
    onYearSelect: (currentMonth: moment.Moment, newMonthVal: string) => void;
    isVisible: boolean;
  }): React.ReactNode => {
    const { month, onYearSelect } = props;
    const selectYears = getYears();

    return (
      <div className="d-flex justify-content-center align-items-center font-weight-bold">
        <div>{moment.months()[month.month()]}</div>
        <div className={styles.yearSelectItem}>
          <select
            className={`custom-select ${styles.yearSelectInput}`}
            value={month.year()}
            onChange={(e) => onYearSelect(month, e.target.value)}
          >
            {selectYears.map((year) => (
              <option value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // render
  return (
    <div className={styles.customDateInput}>
      <SingleDatePicker
        id={name}
        date={date}
        onDateChange={dateChangeHandler}
        focused={focus}
        onFocusChange={focusChangeHandler}
        isOutsideRange={isOutsideOfRange}
        displayFormat={DATE_FORMAT}
        numberOfMonths={1}
        customInputIcon={<FontAwesomeIcon icon="calendar-alt" />}
        placeholder={placeholder ?? tLabels("datePicker.placeholder")}
        readOnly={onlySelectable}
        disabled={disabled}
        block
        hideKeyboardShortcutsPanel
        renderMonthElement={showYearsSelect ? renderMonthElement : undefined}
        initialVisibleMonth={initialVisibleDate ? initialVisibleMonth : undefined}
        showClearDate={isClearable}
      />
    </div>
  );
};

export default DatePicker;
